<template>
  <div
    class="light popup-wrapper"
    :class="{
      'is-open': show,
      'height-auto': fitContentHeight,
    }"
  >
    <slot
      name="action"
      :visible="show"
      :show="() => show = true"
      :hide="() => show = false"
      :toggle="() => show = !show"
    />
    <div
      v-if="show"
      class="popup"
      :class="{
        'fit-parent-width': fitParentWidth,
      }"
      ref="popup"
    >
      <slot
        :visible="show"
        :show="() => show = true"
        :hide="() => show = false"
        :toggle="() => show = !show"
      />
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';
import { ref, watch } from 'vue';

import { freezeBody } from '@kano/utilities/FreezeBody';

const props = withDefaults(defineProps<{
  value?: boolean,
  fitContentHeight?: boolean,
  fitParentWidth?: boolean,
  scrollToTop?: boolean,
}>(), {
  value: false,
  fitContentHeight: false,
  fitParentWidth: true,
  scrollToTop: true,
});

const emit = defineEmits<{
  (event: 'show'): void,
  (event: 'hide'): void,
  (event: 'input', value: boolean): void,
}>();

const show = ref<boolean>(props.value);
watch(() => props.value, () => show.value = props.value);

watch(show, () => {
  if (show.value) {
    emit('show');
  } else {
    emit('hide');
  }
  if (window.innerWidth < 1000) {
    freezeBody(show.value, props.scrollToTop);
  } else {
    freezeBody(false, false);
  }
  emit('input', show.value);
});

const popup = ref(null);
onClickOutside(popup, () => show.value = false);
</script>

<style lang="scss" scoped>
@use '@kanoAssets/styles/breakpoints.scss';

.popup-wrapper {
  position: relative;

  &.is-open:before {
    content: '';
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 999;
    background-color: var(--color-base);

    @include breakpoints.desktop {
      content: none;
    }
  }

  &.height-auto .popup{
    height: fit-content;
  }
}

.popup {
  padding: 24px 0;
  min-width: 100%;
  box-shadow: unset;
  position: fixed;
  top: 24px;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 999;
  height: unset;
  transform: translateY(100vh);
  background-color: var(--color-base-0);
  border-color: var(--color-base-0);
  border-radius: 16px 16px 0 0;
  display: flex;
  flex-direction: column;
  transition: opacity 0.2s linear, transform 0.2s linear,
  visibility 0s linear 0.2s;

  .is-open .popup + &, .is-open & {
    opacity: 1;
    visibility: visible;
    transform: translateY(0);

    @include breakpoints.desktop {
      padding: 8px 0;
      border: solid 1px var(--color-base-8);
      border-radius: 12px;
      position: absolute;
      top: calc(100% + 6px);
      left: 50%;
      transform: translateX(-50%);
      z-index: 10;
      min-height: 183px;
      min-width: 85px;

      overflow: hidden;
    }
  }

  .is-open &.fit-parent-width {
    min-width: auto;
    width: 100%;
  }

  @media screen and (max-height: 500px) {
    height: calc(100vh - #{32px});
  }
}
</style>
